import { useState, useEffect } from 'react';
import { useSearchParams, Link, useNavigate } from 'react-router-dom';
import { ShieldCheck } from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

const ResetPassword = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get('token');

  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  useEffect(() => {
    if (!token) {
      toast.error('Invalid reset link.');
    }
  }, [token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (newPassword.length < 6) {
      toast.error('Password must be at least 6 characters.');
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.error('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const response = await api.post('/auth/reset-password', {
        token,
        new_password: newPassword,
        confirm_password: confirmPassword,
      });
      setSuccess(true);
      toast.success(response.data.message);
      // Clear any existing session
      localStorage.removeItem('token');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reset password. The link may have expired.');
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
        <div className="max-w-md w-full text-center space-y-6">
          <h1 className="font-heading text-4xl text-forest">Invalid Link</h1>
          <p className="text-muted-foreground">This password reset link is invalid or missing.</p>
          <Link
            to="/forgot-password"
            className="inline-block bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
          >
            Request New Link
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center px-4 py-12" data-testid="reset-password-page">
      <div className="max-w-md w-full space-y-8">
        <div className="text-center">
          <h1 className="font-heading text-4xl text-forest mb-2" data-testid="reset-password-heading">
            Set New Password
          </h1>
          <p className="text-muted-foreground">
            {success ? 'Your password has been updated' : 'Choose a strong new password'}
          </p>
        </div>

        {!success ? (
          <form onSubmit={handleSubmit} className="mt-8 space-y-6" data-testid="reset-password-form">
            <div className="space-y-4">
              <div>
                <label htmlFor="newPassword" className="text-sm uppercase tracking-[0.1em] text-forest">
                  New Password
                </label>
                <input
                  id="newPassword"
                  type="password"
                  required
                  minLength={6}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  className="mt-2 w-full px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="new-password-input"
                />
              </div>

              <div>
                <label htmlFor="confirmPassword" className="text-sm uppercase tracking-[0.1em] text-forest">
                  Confirm Password
                </label>
                <input
                  id="confirmPassword"
                  type="password"
                  required
                  minLength={6}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter your password"
                  className="mt-2 w-full px-4 py-3 bg-neutral-warm border border-gold rounded-sm focus:outline-none focus:ring-2 focus:ring-forest"
                  data-testid="confirm-password-input"
                />
              </div>
            </div>

            {newPassword && confirmPassword && newPassword !== confirmPassword && (
              <p className="text-sm text-red-600" data-testid="password-mismatch-error">Passwords do not match</p>
            )}

            <div className="flex items-start space-x-3 p-4 bg-neutral-warm rounded-sm">
              <ShieldCheck className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
              <p className="text-xs text-muted-foreground">
                Resetting your password will log you out from all devices for your security.
              </p>
            </div>

            <button
              type="submit"
              disabled={loading || (newPassword !== confirmPassword)}
              className="w-full bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em] disabled:opacity-50"
              data-testid="reset-submit-button"
            >
              {loading ? 'Resetting...' : 'Reset Password'}
            </button>
          </form>
        ) : (
          <div className="bg-neutral-warm p-8 rounded-sm text-center space-y-4" data-testid="reset-success-message">
            <div className="w-16 h-16 mx-auto rounded-full bg-forest/10 flex items-center justify-center">
              <ShieldCheck className="w-8 h-8 text-forest" />
            </div>
            <p className="text-forest leading-relaxed">
              Your password has been reset and you've been logged out from all devices.
            </p>
            <button
              onClick={() => navigate('/login')}
              className="inline-block bg-forest text-ivory px-8 py-4 rounded-none hover:bg-gold hover:text-forest transition-colors text-sm uppercase tracking-[0.1em]"
              data-testid="go-to-login-button"
            >
              Log In with New Password
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default ResetPassword;
